import React, { useMemo, useState } from 'react';
import ImportWizardBase from '#/modules/admin/dataImport/components/base/ImportWizardBase';
import PasteExcelDataStep from '../components/CapitalNeedExcelImport/PasteExcelDataStep';
import CapitalNeedPreviewStep from '../components/CapitalNeedExcelImport/PreviewStep';
import { processExcel } from '../utils/excelProcessing';
import useBulkCreateCapitalNeed from '../hooks/useBulkCreateCapitalNeedMutation';
import { CapitalNeedInput, CapitalNeedResponse } from '#/graphql/types';
import useToast from '#/common/hooks/useToast';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CapitalNeedExcelImportPage: React.FC = () => {
  const { alert: showAlert } = useToast();
  const navigate = useNavigate();
  const { bulkCreateCapitalNeed } = useBulkCreateCapitalNeed();

  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId');
  const tenantId = searchParams.get('tenantId');

  const [pastedText, setPastedText] = useState('');
  const [importing, setImporting] = useState(false);

  const previewRows = useMemo<CapitalNeedInput[]>(() => {
    return pastedText.trim().length > 0 ? processExcel(pastedText) : [];
  }, [pastedText]);

  const steps = useMemo(
    () => [
      {
        component: <PasteExcelDataStep value={pastedText} onChange={setPastedText} />,
        isValid: pastedText.trim().length > 0,
      },
      {
        component: <CapitalNeedPreviewStep rows={previewRows} />,
      },
    ],
    [pastedText, previewRows],
  );

  const handleSubmit = async () => {
    try {
      setImporting(true);
      const enrichedRows = previewRows.map((row) => ({
        ...row,
        status: 'draft',
        assetId: assetId ?? '',
        tenantId: tenantId ?? '',
      }));
      const result = (await bulkCreateCapitalNeed(enrichedRows)) as CapitalNeedResponse;
      setImporting(false);

      if (assetId && result?.status === 200) {
        navigate(`/assets/${assetId}/capital-needs`);
      }

      showAlert('success', 'Import completed successfully!');
    } catch (error: any) {
      setImporting(false);
      showAlert('error', error.message || 'Import failed');
    }
  };

  return (
    <ImportWizardBase
      title="Import Capital Needs"
      steps={steps}
      onSubmit={handleSubmit}
      insertingRecords={importing}
      submitLabel="Import"
      showButtonsOnBottom={false}
      goBackByHowManyPages={2}
    />
  );
};

export default React.memo(CapitalNeedExcelImportPage);
